import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../shared/Icons';
import { Button } from '../shared/Button';
import { NotificationBell } from './NotificationBell';
import { UserMenu } from './UserMenu';
import { useAuth } from '../../contexts/auth-context';

// Pathname → breadcrumb label for the current section. Keys are exact routes (see App.tsx);
// dynamic sub-routes (e.g. the clip editor) are handled in crumbsFor below.
const CRUMB: Record<string, string> = {
  '/': 'Home',
  '/clips': 'Video clips',
  '/rule-tests': 'Rule tests',
  '/exams': 'Video clip exams',
  '/bookmarked': 'Bookmarked',
  '/documents': 'Documents',
  '/leaderboard': 'Leaderboard',
  '/notifications': 'Notifications',
  '/settings': 'Settings',
  '/settings/notifications': 'Settings',
  '/settings/security': 'Settings',
  '/invites': 'Users',
  '/invites/users': 'Users',
  '/federations': 'Federations',
  '/sports': 'Sports',
  '/rulebooks': 'Rulebooks',
  '/admin/clips': 'Clips',
};

// One crumb in the trail. `to` makes it a back-link (intermediate crumbs); the last crumb
// is plain text (you're already there).
interface Crumb {
  label: string;
  to?: string;
}

// Build the breadcrumb trail for a route. Most pages are a single section crumb; nested
// routes (the clip editor) return a parent link + the leaf so the admin can step back up.
function crumbsFor(pathname: string): Crumb[] {
  // /admin/clips/:id/edit — show "Clips / Edit clip", Clips linking back to the list.
  if (/^\/admin\/clips\/[^/]+\/edit$/.test(pathname)) {
    return [{ label: 'Clips', to: '/admin/clips' }, { label: 'Edit clip' }];
  }
  return [{ label: CRUMB[pathname] ?? 'Home' }];
}

interface TopbarProps {
  // Opens the mobile nav drawer. Rendered as the hamburger below lg, where the sidebar is
  // off-canvas; at lg and up the sidebar is always visible and the button is hidden.
  onOpenNav?: () => void;
}

export function Topbar({ onOpenNav }: TopbarProps) {
  const { pathname } = useLocation();
  const { role } = useAuth();
  const crumbs = crumbsFor(pathname);
  // The super_admin operates a global console, not a federation app — say so in the crumb root.
  const brand = role === 'super_admin' ? 'ProRef Console' : 'ProRef';

  return (
    <div className="sticky top-0 z-10 flex h-16 items-center gap-2 border-b border-line bg-bg/85 px-4 backdrop-blur-md backdrop-saturate-150 sm:gap-3.5 sm:px-6 lg:px-7">
      {/* Hamburger — the only way to reach navigation below lg, where the sidebar is off-canvas. */}
      {onOpenNav && (
        <Button
          variant="ghost"
          icon
          className="h-9 w-9 flex-none lg:hidden"
          onClick={onOpenNav}
          title="Open navigation"
          aria-label="Open navigation"
        >
          <Icon.menu className="w-4 h-4" />
        </Button>
      )}

      {/* Breadcrumbs: brand root + a trail. Intermediate crumbs (with `to`) link back up;
          the last crumb is the current page, rendered in ink as plain text. The brand root and
          any intermediate crumbs drop below sm — on a phone the leaf ("which page am I on")
          is the only part carrying information, and the full trail pushed the actions off-screen. */}
      <div className="flex min-w-0 items-center gap-2 text-[13px] text-muted">
        <span className="hidden sm:inline">{brand}</span>
        {crumbs.map((crumb, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <span
              key={crumb.to ?? crumb.label}
              className={'flex min-w-0 items-center gap-2 ' + (isLast ? '' : 'hidden sm:flex')}
            >
              <span className="hidden text-muted-2 sm:inline">/</span>
              {crumb.to && !isLast ? (
                <Link to={crumb.to} className="transition-colors hover:text-ink">
                  {crumb.label}
                </Link>
              ) : (
                <span className="truncate font-medium text-ink">{crumb.label}</span>
              )}
            </span>
          );
        })}
      </div>

      <div className="flex-1" />

      {/* Search affordance (visual only this slice) */}
      <div className="hidden h-9 w-80 items-center gap-2 rounded-sm border border-line bg-surface px-3 text-[13px] text-muted md:flex">
        <Icon.search className="w-3.5 h-3.5" />
        <span>Search clips, rules, people…</span>
        <span className="flex-1" />
        <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] border border-line-2 bg-surface-2 px-1 font-mono text-[10px] text-ink-3">
          ⌘
        </span>
        <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-[4px] border border-line-2 bg-surface-2 px-1 font-mono text-[10px] text-ink-3">
          K
        </span>
      </div>

      <NotificationBell />

      <div className="hidden h-[22px] w-px bg-line sm:block" />

      <UserMenu />
    </div>
  );
}
